import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { NEAR_ME, PATHS } from 'src/common/constants';
import captureException from 'src/common/utils/captureException';
import dynamicImport from 'src/common/utils/dynamicImport';
import prependLocale from 'src/common/utils/prependLocale';

type UseSearchUrl = {
  /**
   * Loads the `getSearchUrl` functionality.
   */
  loadSearchUrlFn: () => Promise<GetSearchUrl>;
  /**
   * Redirects to the search page for the provided `placeId`.
   */
  redirectToSearchUrl: (placeId: string, params?: URLSearchParams, locale?: string) => void;
  /**
   * Indicate that a search is currently in progress.
   */
  isSearching: boolean;
};

type GetSearchUrl = (placeId: string, locale: string) => Promise<string>;

/**
 * Lazy loads and returns the `getSearchUrl` function.
 */
const loadSearchUrlFn = async (): Promise<GetSearchUrl> => dynamicImport(
  () => import(/* webpackChunkName: "getSearchUrl" */ './getSearchUrl')
    .then((module) => module.default),
  // fallback to the search page without the search url
  () => (locale: string) => prependLocale(PATHS.SEARCH, locale),
);

export const mergeUrls = (url: string, params?: URLSearchParams): string => {
  const newUrl = new URL(url);
  if (params) {
    params.forEach((value, key) => {
      if (key === 'place_id') {
        // Only include the `place_id` if there is no location slug.
        if (newUrl.pathname === PATHS.SEARCH) {
          // `place_id` can only have a single entry.
          if (value !== NEAR_ME) {
            newUrl.searchParams.set(key, value);
          }
        }
      } else if (key === 'page') {
        // Handles any duplicates from merge
        newUrl.searchParams.set(key, value);
      } else {
        newUrl.searchParams.append(key, value);
      }
    });
  }
  newUrl.searchParams.sort();
  return newUrl.toString();
};

/**
 * Returns search related functionality.
 */
const useSearchUrl = (): UseSearchUrl => {
  const [isSearching, setIsSearching] = useState(false);
  const router = useRouter();

  const redirectToSearchUrl = useCallback(
    (placeId: string, params?: URLSearchParams, locale?: string) => {
      setIsSearching(true);
      // As long as we have previously called `loadSearchUrlFn`, the module will be immediately
      // resolved, so there should be no delay on the code loading side.
      loadSearchUrlFn().then(async (getSearchUrl) => {
        try {
          const searchUrl = await getSearchUrl(placeId, locale);
          setIsSearching(false);
          if (typeof window !== 'undefined') {
            if (params) {
              // search term is different from initial location
              const mergeUrl = mergeUrls(searchUrl, params);
              router.push(mergeUrl);
              return;
            }

            // Apply the page param when other params are not present
            router.push(searchUrl);
          }
        // Upon receiving the search url, the user is redirected to the url that is controlled by
        // a different app, so `isSearching` does not get set back to `false` so that the UI state
        // stays in the searching state until the redirect kicks in.
        } catch (err) {
          captureException(err);
          setIsSearching(false);
        }
      }).catch((err2) => {
        captureException(err2);
        setIsSearching(false);
      });
    },
    [router],
  );

  return {
    redirectToSearchUrl,
    isSearching,
    loadSearchUrlFn,
  };
};

export default useSearchUrl;
